import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  model,
  signal,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ToastrService } from 'ngx-toastr';
import { SendCommunicationFormComponent } from '../communication/send-communication-form.component';
import { SaleCommunicationPanelComponent } from './sale-communication-panel.component';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { ChargeService } from '../../services/charge.service';
import { SaleService } from '../../services/sale.service';
import { apiFailureMessage, httpErrorApiMessage } from '../../core/api-response-utils';
import { calendarDaysUntil, formValueToApiDateTime, formatLocalDateOnly, parseLocalDate } from '../../core/date-utils';
import {
  ChargeOperationalStatusEnum,
  type ChargeDTO,
  type ChargeOperationalStatusEnumValue
} from '../../core/models';

type FollowupTab = 'email' | 'bitacora' | 'seguimiento';

@Component({
  selector: 'app-charge-followup-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    DatePickerModule,
    FloatLabelModule,
    PERMISSION_UI_DIRECTIVES,
    SendCommunicationFormComponent,
    SaleCommunicationPanelComponent
  ],
  templateUrl: './charge-followup-dialog.component.html',
  styles: [
    `
      :host ::ng-deep .charge-followup-dialog.p-dialog {
        max-height: 90vh;
      }
      :host ::ng-deep .charge-followup-dialog .p-dialog-content {
        flex: 1 1 auto;
        min-height: 0;
        display: flex;
        flex-direction: column;
      }
      :host ::ng-deep .charge-followup-shell {
        min-height: 0;
      }
      :host ::ng-deep .charge-followup-content {
        overscroll-behavior: contain;
      }

      .followup-nav__title {
        letter-spacing: 0.06em;
      }

      .followup-tab {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        width: 100%;
        padding: 0.625rem 0.75rem;
        border: 1px solid transparent;
        border-radius: 0.625rem;
        background: transparent;
        text-align: left;
        cursor: pointer;
        transition:
          background-color 0.15s ease,
          border-color 0.15s ease,
          box-shadow 0.15s ease,
          color 0.15s ease;
      }

      .followup-tab:hover:not(.followup-tab--active) {
        background: rgb(255 255 255 / 0.65);
        border-color: rgb(226 232 240);
      }

      .followup-tab--active {
        background: white;
        border-color: rgb(199 210 254);
        box-shadow:
          0 1px 2px rgb(15 23 42 / 0.06),
          0 0 0 1px rgb(238 242 255);
        position: relative;
      }

      .followup-tab--active::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0.5rem;
        bottom: 0.5rem;
        width: 3px;
        border-radius: 0 2px 2px 0;
        background: rgb(79 70 229);
      }

      .followup-tab__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 2.125rem;
        height: 2.125rem;
        border-radius: 0.5rem;
        background: rgb(226 232 240);
        color: rgb(100 116 139);
        font-size: 0.9375rem;
        transition:
          background-color 0.15s ease,
          color 0.15s ease;
      }

      .followup-tab--active .followup-tab__icon {
        background: rgb(238 242 255);
        color: rgb(79 70 229);
      }

      .followup-tab__text {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
        min-width: 0;
      }

      .followup-tab__label {
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.2;
        color: rgb(71 85 105);
      }

      .followup-tab--active .followup-tab__label {
        font-weight: 600;
        color: rgb(67 56 202);
      }

      .followup-tab__hint {
        font-size: 0.6875rem;
        line-height: 1.2;
        color: rgb(148 163 184);
      }

      .followup-tab--active .followup-tab__hint {
        color: rgb(129 140 248);
      }
    `
  ]
})
export class ChargeFollowupDialogComponent {
  visible = model(false);
  chargeId = input.required<number>();
  saleId = input<number | null>(null);
  title = input('Seguimiento');
  subtitle = input<string | null>(null);

  activeTab = signal<FollowupTab>('email');
  customerEmail = signal('');
  chargeLoading = signal(false);
  savingCommitment = signal(false);
  chargeDueDate = signal<string | null>(null);
  chargeStatus = signal<ChargeOperationalStatusEnumValue | null>(null);

  readonly sendFormRef = viewChild<SendCommunicationFormComponent>('sendForm');
  readonly communicationPanelRef = viewChild<SaleCommunicationPanelComponent>('bitacoraPanel');

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly chargeService = inject(ChargeService);
  private readonly saleService = inject(SaleService);
  private readonly fb = inject(FormBuilder);
  private readonly toastr = inject(ToastrService);

  readonly seguimientoForm = this.fb.nonNullable.group({
    commitmentDate: this.fb.control<Date | null>(null)
  });

  constructor() {
    effect(() => {
      if (!this.visible()) {
        this.customerEmail.set('');
        this.chargeLoading.set(false);
        this.savingCommitment.set(false);
        this.chargeDueDate.set(null);
        this.chargeStatus.set(null);
        this.seguimientoForm.reset({ commitmentDate: null });
        return;
      }
      this.resolveCustomerEmail(this.chargeId(), this.saleId());
    });
  }

  /** Recibo vencido + fecha compromiso en formulario ya pasada. */
  isBrokenCommitment(): boolean {
    const status = this.chargeStatus();
    if (
      status === ChargeOperationalStatusEnum.PAID ||
      status === ChargeOperationalStatusEnum.CANCELLED
    ) {
      return false;
    }
    const due = this.chargeDueDate();
    if (!due) return false;
    const daysUntilDue = calendarDaysUntil(due);
    if (daysUntilDue == null || daysUntilDue >= 0) return false;

    const raw = this.seguimientoForm.controls.commitmentDate.value;
    if (!raw) return false;
    const commitmentIso = formatLocalDateOnly(raw instanceof Date ? raw : new Date(raw));
    const daysUntilCommitment = calendarDaysUntil(commitmentIso);
    return daysUntilCommitment != null && daysUntilCommitment < 0;
  }

  onVisibleChange(visible: boolean): void {
    this.visible.set(visible);
    if (!visible) {
      this.activeTab.set('email');
    }
  }

  onCommunicationSent(): void {
    this.communicationPanelRef()?.onCommunicationSent();
  }

  onSendFormStateChange(): void {
    this.cdr.markForCheck();
  }

  selectTab(tab: FollowupTab): void {
    this.activeTab.set(tab);
    if (tab === 'bitacora') {
      this.refreshBitacora();
    }
  }

  saveCommitmentDate(): void {
    const chargeId = this.chargeId();
    const raw = this.seguimientoForm.controls.commitmentDate.value;
    const commitmentDate = formValueToApiDateTime(raw) ?? null;

    this.savingCommitment.set(true);
    this.chargeService.updateCommitmentDate(chargeId, commitmentDate).subscribe({
      next: (response) => {
        if (!this.visible() || this.chargeId() !== chargeId) {
          this.savingCommitment.set(false);
          return;
        }
        if (!response.ok) {
          this.toastr.error(apiFailureMessage(response, 'No se pudo guardar la fecha compromiso'));
          this.savingCommitment.set(false);
          return;
        }
        const saved = parseLocalDate(response.body?.cxc?.commitmentDate ?? undefined);
        this.seguimientoForm.patchValue({ commitmentDate: saved });
        this.applyChargeContext(response.body);
        this.toastr.success(response.message ?? 'Fecha compromiso actualizada');
        this.savingCommitment.set(false);
        this.cdr.markForCheck();
      },
      error: (err) => {
        if (this.visible() && this.chargeId() === chargeId) {
          this.toastr.error(httpErrorApiMessage(err, 'Error al guardar la fecha compromiso'));
          this.savingCommitment.set(false);
          this.cdr.markForCheck();
        }
      }
    });
  }

  private refreshBitacora(): void {
    this.communicationPanelRef()?.load();
  }

  private resolveCustomerEmail(chargeId: number, saleId: number | null): void {
    this.customerEmail.set('');
    this.chargeLoading.set(true);
    this.chargeService.searchCharges({ id: chargeId }, { page: 0, size: 1 }).subscribe({
      next: (response) => {
        if (!this.visible() || this.chargeId() !== chargeId) {
          this.chargeLoading.set(false);
          return;
        }
        const charge = response.body?.[0];
        this.applyChargeContext(charge);
        const commitment = parseLocalDate(charge?.cxc?.commitmentDate ?? undefined);
        this.seguimientoForm.patchValue({ commitmentDate: commitment });

        const fromCharge = charge?.sale?.customer?.email?.trim();
        if (fromCharge) {
          this.customerEmail.set(fromCharge);
          this.chargeLoading.set(false);
          this.cdr.markForCheck();
          return;
        }
        const resolvedSaleId = charge?.saleId ?? saleId;
        if (resolvedSaleId == null) {
          this.chargeLoading.set(false);
          this.cdr.markForCheck();
          return;
        }
        this.saleService.getSaleById(resolvedSaleId).subscribe({
          next: (saleResponse) => {
            if (!this.visible() || this.chargeId() !== chargeId) {
              this.chargeLoading.set(false);
              return;
            }
            const email = saleResponse.body?.customer?.email?.trim();
            if (email) {
              this.customerEmail.set(email);
            }
            this.chargeLoading.set(false);
            this.cdr.markForCheck();
          },
          error: () => {
            if (this.visible() && this.chargeId() === chargeId) {
              this.chargeLoading.set(false);
              this.cdr.markForCheck();
            }
          }
        });
      },
      error: () => {
        if (this.visible() && this.chargeId() === chargeId) {
          this.chargeLoading.set(false);
          this.cdr.markForCheck();
        }
      }
    });
  }

  private applyChargeContext(charge: ChargeDTO | undefined): void {
    this.chargeDueDate.set(charge?.dueDate ?? null);
    this.chargeStatus.set(charge?.status ?? null);
  }
}
